import type { ISerialize } from '@/objects/ISerialize';
import { formatDateIso } from '@/utilities/dateutils';

export class ProductionKeepAlive implements ISerialize {
    public DeviceId: string;

    public DisplayHeight: number | null;

    public DisplayRowHeight: number | null;

    public DisplayWidth: number | null;

    public EventId: number;

    public LastSeen: Date;

    public NumberOfLines: number | null;

    public NumberOfLinesCalculated: number | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        DeviceId?: string | null;
        DisplayHeight?: number | null;
        DisplayRowHeight?: number | null;
        DisplayWidth?: number | null;
        EventId?: number;
        LastSeen?: Date;
        NumberOfLines?: number | null;
        NumberOfLinesCalculated?: number | null;
    }) {
        this.DeviceId = values.DeviceId ?? '';

        this.DisplayHeight = values.DisplayHeight ?? null;

        this.DisplayRowHeight = values.DisplayRowHeight ?? null;

        this.DisplayWidth = values.DisplayWidth ?? null;

        this.EventId = values.EventId ?? 0;

        this.LastSeen = values.LastSeen ?? new Date('1970-01-01T00:00:00.000');

        this.NumberOfLines = values.NumberOfLines ?? null;

        this.NumberOfLinesCalculated = values.NumberOfLinesCalculated ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"DeviceId": ${JSON.stringify(this.DeviceId)}` +
            ',' +
            `"DisplayHeight": ${JSON.stringify(this.DisplayHeight)}` +
            ',' +
            `"DisplayRowHeight": ${JSON.stringify(this.DisplayRowHeight)}` +
            ',' +
            `"DisplayWidth": ${JSON.stringify(this.DisplayWidth)}` +
            ',' +
            `"EventId": ${JSON.stringify(this.EventId)}` +
            ',' +
            `"LastSeen": ${'"' + formatDateIso(this.LastSeen) + '"'}` +
            ',' +
            `"NumberOfLines": ${JSON.stringify(this.NumberOfLines)}` +
            ',' +
            `"NumberOfLinesCalculated": ${JSON.stringify(this.NumberOfLinesCalculated)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ProductionKeepAlive from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        DeviceId?: string | null;
        DisplayHeight?: number | null;
        DisplayRowHeight?: number | null;
        DisplayWidth?: number | null;
        EventId?: number;
        LastSeen?: Date;
        NumberOfLines?: number | null;
        NumberOfLinesCalculated?: number | null;
    }): ProductionKeepAlive {
        return new ProductionKeepAlive(values);
    }

    public static _FromServerJson(json: any): ProductionKeepAlive {
        return new ProductionKeepAlive({
            DeviceId: json.DeviceId ?? '',
            DisplayHeight: json.DisplayHeight ?? null,
            DisplayRowHeight: json.DisplayRowHeight ?? null,
            DisplayWidth: json.DisplayWidth ?? null,
            EventId: json.EventId,
            LastSeen: json.LastSeen instanceof Date ? json.LastSeen : new Date(json.LastSeen),
            NumberOfLines: json.NumberOfLines ?? null,
            NumberOfLinesCalculated: json.NumberOfLinesCalculated ?? null,
        });
    }

    /**
     * Creates an instance of ProductionKeepAlive from another instance of ProductionKeepAlive.
     * @param other The ProductionKeepAlive you want to clone
     * @returns A cloned ProductionKeepAlive
     */
    public static FromOther(other: ProductionKeepAlive): ProductionKeepAlive {
        return ProductionKeepAlive._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ProductionKeepAlive.FromOther(this);
    }

    static NAME_OF_CLASS = 'ProductionKeepAlive';
}
